import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import PageHeader from '../../components/PageHeader';
import DataState from '../../components/DataState';
import { useSession } from '../../permissions/SessionContext';
import { hasPermissions } from '../../permissions/check';
import {
  fetchGuardians,
  fetchGuardianLinkRequests,
  createGuardianLinkRequest,
  cancelGuardianLinkRequest,
} from '../../api/academy';
import { academyScope } from './scope';
import { colors } from '../../design-system/tokens';

const STUDENT_STATUS_LABEL = { active: '재원', paused: '휴원', withdrawn: '퇴원' };

const REQ_STATUS = {
  pending: { label: '요청 대기', tone: colors.status.info },
  accepted: { label: '연결됨', tone: colors.status.success },
  rejected: { label: '거절됨', tone: colors.status.danger },
  expired: { label: '만료', tone: colors.status.neutral },
  cancelled: { label: '취소됨', tone: colors.status.neutral },
};
const entityId = (value) => String(value?.id ?? value?._id ?? value ?? '');
const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('ko-KR', {
        month: 'long',
        day: 'numeric',
      })
    : '';

const Parents = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  // 보호자 앱 연결 '요청' 생성과 요청 상태 열람은 manager 이상 (실제 차단은 backend parents.manage)
  const canManageLink = hasPermissions(session, ['parents.manage']);

  const [rows, setRows] = useState([]);
  const [requestsByStudent, setRequestsByStudent] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [requestStatusError, setRequestStatusError] = useState('');
  const [actingStudentId, setActingStudentId] = useState(null);

  const loadRequests = useCallback(async () => {
    if (!canManageLink) {
      setRequestStatusError('');
      return;
    }
    try {
      const list = await fetchGuardianLinkRequests(academyScope(session));
      // 목록은 createdAt desc → studentId 별 첫 항목이 최신
      const map = {};
      for (const r of list) {
        if (!map[r.studentId]) map[r.studentId] = r;
      }
      setRequestsByStudent(map);
      setRequestStatusError('');
    } catch {
      // 상태를 모르는 상태에서 다시 보내면 동일 보호자에게 중복 요청을 만들 수 있다.
      // 연락처 목록은 계속 보여주되, 요청/취소는 상태를 다시 읽을 때까지 잠근다.
      setRequestsByStudent({});
      setRequestStatusError(
        '보호자 연결 요청 상태를 확인하지 못했어요. 중복 요청을 막기 위해 새 요청과 취소를 잠시 멈췄어요.',
      );
    }
  }, [canManageLink, session]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [guardians] = await Promise.all([
        fetchGuardians(academyScope(session)),
        loadRequests(),
      ]);
      setRows(guardians);
    } catch (e) {
      setError(e?.response?.data?.message || '학부모 목록을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [loadRequests, session]);

  useEffect(() => {
    load();
  }, [load]);

  const requestLink = async (studentId) => {
    if (actingStudentId) return; // 중복 탭 방어
    setActingStudentId(studentId);
    setError('');
    setInfo('');
    try {
      await createGuardianLinkRequest({ studentId, ...academyScope(session) });
      // 회원 존재 여부·상세를 노출하지 않는 일반 안내
      setInfo('보호자에게 연결 요청을 보냈어요. 보호자가 앱에서 수락하면 연결됩니다.');
      await loadRequests();
    } catch (e) {
      setError(e?.response?.data?.message || '연결 요청을 보내지 못했어요.');
    } finally {
      setActingStudentId(null);
    }
  };

  const cancelLink = async (studentId, requestId) => {
    if (actingStudentId) return; // 중복 탭 방어
    setActingStudentId(studentId);
    setError('');
    setInfo('');
    try {
      await cancelGuardianLinkRequest(requestId, academyScope(session));
      setInfo('연결 요청을 취소했어요. 필요하면 다시 요청할 수 있어요.');
      await loadRequests();
    } catch (e) {
      setError(e?.response?.data?.message || '요청을 취소하지 못했어요.');
    } finally {
      setActingStudentId(null);
    }
  };

  const studentControl = (s) => {
    const studentId = entityId(s);
    const req = requestsByStudent[studentId];
    if (requestStatusError) {
      return (
        <Typography variant="caption" sx={{ color: colors.status.warning.fg }}>
          연결 상태 확인 필요 · 위 안내에서 다시 시도해 주세요.
        </Typography>
      );
    }
    // 승인 대기 → 상태 칩 + 취소 버튼 (원장이 재요청 전 정리할 수 있게)
    if (req && req.status === 'pending') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={REQ_STATUS.pending.label}
            sx={{
              bgcolor: REQ_STATUS.pending.tone.bg,
              color: REQ_STATUS.pending.tone.fg,
            }}
          />
          <Button
            size="small"
            color="error"
            sx={{ minWidth: 'auto', px: 0.75 }}
            onClick={() => cancelLink(studentId, req.id)}
            disabled={Boolean(actingStudentId)}
          >
            요청 취소
          </Button>
          <Typography variant="caption" sx={{ color: colors.text.muted }}>
            보호자 앱 수락 대기{req.expiresAt ? ` · ${formatDate(req.expiresAt)}까지` : ''}
          </Typography>
        </Box>
      );
    }
    // 과거 accepted 요청이 있어도 현재 학생 연결이 해제됐다면 terminal history일 뿐이다.
    // 현재 guardianMemberId 연결이 유지되는 학생만 연결 완료로 표시한다.
    if (s.guardianLinked && req && req.status === 'accepted') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={REQ_STATUS.accepted.label}
            sx={{
              bgcolor: REQ_STATUS.accepted.tone.bg,
              color: REQ_STATUS.accepted.tone.fg,
            }}
          />
          <Typography variant="caption" sx={{ color: colors.text.muted }}>
            연결을 확인했어요. 출결 기록을 진행할 수 있어요.
          </Typography>
        </Box>
      );
    }
    // 요청 없음/거절/만료/취소 → 다시 요청 가능 (퇴원 원생 제외)
    if (s.status === 'withdrawn') {
      return (
        <Typography variant="caption" sx={{ color: colors.text.muted }}>
          퇴원
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Button
          size="small"
          onClick={() => requestLink(studentId)}
          disabled={Boolean(actingStudentId)}
        >
          앱 연결 요청
        </Button>
        <Typography variant="caption" sx={{ color: colors.text.muted }}>
          {req?.status === 'rejected'
            ? '보호자가 거절했어요. 사유는 노출하지 않으니 연락처·대상을 확인한 뒤 다시 요청하세요.'
            : req?.status === 'expired'
              ? '요청 유효기간이 지났어요. 보호자 연락처를 확인한 뒤 다시 요청하세요.'
              : req?.status === 'cancelled'
                ? '이전 요청을 취소했어요. 보호자 연락처를 확인한 뒤 다시 요청하세요.'
                : '보호자 연락처를 확인한 뒤 요청하세요. 회원 존재 여부는 표시하지 않습니다.'}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <PageHeader
        title="학부모 관리"
        description="보호자 연락처는 원생 정보에서 파생돼요(별도 저장 없음). 보호자 정보 수정은 수강생 관리에서 해당 원생을 열어 변경해요. 연락처는 안전을 위해 가운데를 가려 표시합니다."
      />

      {info && (
        <Alert role="status" severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>
          {info}
        </Alert>
      )}

      {requestStatusError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={loadRequests}>
              상태 다시 확인
            </Button>
          }
        >
          {requestStatusError}
        </Alert>
      )}

      <DataState
        loading={loading}
        error={error}
        onRetry={load}
        empty={rows.length === 0}
        emptyText="보호자 연락처가 등록된 원생이 없어요. 수강생 관리에서 보호자 연락처를 먼저 입력해 주세요."
      >
        <Paper sx={{ p: 1 }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>보호자</TableCell>
                  <TableCell>연락처</TableCell>
                  <TableCell>앱 연결</TableCell>
                  <TableCell>연결된 원생</TableCell>
                  {canManageLink && <TableCell>보호자 앱 연결 요청</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((g) => (
                  <TableRow key={g.id} hover>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {g.guardianName || '이름 미등록'}
                    </TableCell>
                    <TableCell sx={{ color: colors.text.muted, whiteSpace: 'nowrap' }}>
                      {g.phoneMasked || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={g.linked ? '앱 연결됨' : '미연결'}
                        sx={{
                          bgcolor: g.linked
                            ? colors.status.success.bg
                            : colors.status.neutral.bg,
                          color: g.linked
                            ? colors.status.success.fg
                            : colors.status.neutral.fg,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: colors.text.muted }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {g.students.map((s) => (
                          <Chip
                            key={entityId(s)}
                            size="small"
                            label={`${s.name}${s.grade ? ` · ${s.grade}` : ''}${
                              s.status && s.status !== 'active'
                                ? ` · ${STUDENT_STATUS_LABEL[s.status] || '상태 확인 필요'}`
                                : ''
                            }`}
                            sx={{
                              bgcolor: colors.brand.primarySoft,
                              color: colors.brand.primary,
                            }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    {canManageLink && (
                      <TableCell>
                        {g.linked ? (
                          <Typography variant="caption" sx={{ color: colors.text.muted }}>
                            연결 완료
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {g.students.map((s) => (
                              <Box
                                key={entityId(s)}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                              >
                                <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                                  {s.name}
                                </Typography>
                                {studentControl(s)}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DataState>

      {!loading && !error && rows.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          원생 등록 화면에서 보호자 연락처를 입력한 뒤 연결 요청을 보낼 수 있어요.
          <Button size="small" onClick={() => navigate('/owner-v2/students')} sx={{ ml: 1 }}>
            수강생 관리 열기
          </Button>
        </Alert>
      )}
    </Box>
  );
};

export default Parents;
